import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sparkles, Check, ShieldCheck, Info } from 'lucide-react';
import useEntitlement from '../hooks/useEntitlement';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * MembershipPage — Prompt #8 §54.
 *
 * States rendered from `/api/me/subscription`:
 *   NONE      → "Reflections Free" + Explore Premium
 *   TRIALING  → Trial ends [date]
 *   ACTIVE    → Renews [date] + Manage Membership (placeholder — no billing yet)
 *   CANCELLED → "Premium access ends [date]"
 *   EXPIRED   → "Restart Premium"
 *
 * NO LIVE BILLING. Selecting a plan does NOT start a charge — it opens the
 * paywall confirmation which currently says "Coming soon" until the payment
 * provider phase (Phase C) is approved.
 */
const fmtDate = (iso) => {
  if (!iso) return null;
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }); }
  catch { return null; }
};

const MembershipPage = () => {
  const navigate = useNavigate();
  const { data, loading } = useEntitlement();
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [showComingSoon, setShowComingSoon] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/subscription/plans`)
      .then((r) => (r.ok ? r.json() : { plans: [] }))
      .then((d) => setPlans(d.plans || []))
      .catch(() => setPlans([]))
      .finally(() => setPlansLoading(false));
  }, []);

  const status = data?.status || 'NONE';
  const isPremium = !!data?.isPremium;
  const renews = fmtDate(data?.renewsAt);
  const trialEnds = fmtDate(data?.trialEndsAt);

  return (
    <div className="pb-24 min-h-screen bg-[#F6F1E7]" data-testid="membership-page">
      <div className="px-5 pt-5">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-[12px] text-stone-500 mb-3" data-testid="membership-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="rbr-eyebrow" style={{ color: '#C79A4A' }}>Membership</p>
        <h1 className="text-3xl leading-tight text-stone-800 mt-1" style={{ fontFamily: "'DM Serif Display', serif" }}>
          Go deeper with Reflections.
        </h1>
        <p className="text-sm text-stone-500 mt-1">More space to breathe, reflect, heal, rest, and grow.</p>
      </div>

      {/* Current status */}
      {!loading && (
        <div className="mx-5 mt-6 rounded-2xl p-5 border border-stone-200" style={{ background: isPremium ? 'linear-gradient(160deg, #173F35 0%, #0D2B24 100%)' : '#FFFCF6', color: isPremium ? '#F6F1E7' : undefined }} data-testid="membership-status-card">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" style={{ color: '#C79A4A' }} />
            <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>Your membership</p>
          </div>
          <p className="text-xl mt-1" style={{ fontFamily: "'DM Serif Display', serif" }}>
            {status === 'ACTIVE' && 'Reflections Premium'}
            {status === 'TRIALING' && 'Reflections Premium — Trial'}
            {status === 'CANCELLED' && 'Premium (cancelling)'}
            {status === 'EXPIRED' && 'Reflections Free'}
            {status === 'NONE' && (isPremium ? 'Reflections Premium' : 'Reflections Free')}
          </p>
          {status === 'TRIALING' && trialEnds && <p className="text-[12px] mt-1" style={{ color: 'rgba(246,241,231,0.75)' }}>Trial ends {trialEnds}</p>}
          {status === 'ACTIVE'    && renews    && <p className="text-[12px] mt-1" style={{ color: 'rgba(246,241,231,0.75)' }}>Renews {renews}</p>}
          {status === 'CANCELLED' && renews    && <p className="text-[12px] mt-1" style={{ color: 'rgba(246,241,231,0.75)' }}>Premium access ends {renews}</p>}
          {status === 'NONE'      && <p className="text-[12px] mt-1 text-stone-500">Your journal, community, mood check-ins, breathing, and daily reflection are free — always.</p>}
        </div>
      )}

      {/* Plans */}
      {!isPremium && (
        <div className="px-5 mt-6">
          <p className="rbr-eyebrow mb-3" style={{ color: '#C79A4A' }}>Choose a plan</p>
          {plansLoading ? (
            <p className="text-stone-500 text-sm">Loading plans…</p>
          ) : plans.length === 0 ? (
            <div className="rounded-2xl p-5 bg-[#FFFCF6] border border-stone-200 text-center" data-testid="membership-plans-empty">
              <Info className="w-5 h-5 text-stone-400 mx-auto mb-2" />
              <p className="text-sm text-stone-700" style={{ fontFamily: "'DM Serif Display', serif" }}>Premium plans coming soon.</p>
              <p className="text-[12px] text-stone-500 mt-1">Rowdy is finalizing the launch. Your free experience continues in the meantime.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {plans.map((p) => (
                <button
                  key={p.id}
                  onClick={() => { setSelectedPlanId(p.id); setShowComingSoon(true); }}
                  data-testid={`membership-plan-${p.billingInterval}`}
                  className={`text-left rounded-2xl p-4 border transition-all ${selectedPlanId === p.id ? 'border-[#C79A4A] bg-[#FFF6DF]' : 'border-stone-200 bg-[#FFFCF6] hover:border-[#C79A4A]/50'}`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-lg text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>{p.name}</p>
                      {p.description && <p className="text-[12px] text-stone-500 mt-0.5">{p.description}</p>}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-semibold text-stone-800">{p.displayPrice || '—'}</p>
                      <p className="text-[11px] text-stone-500">per {p.billingInterval}</p>
                    </div>
                  </div>
                  {p.trialDays > 0 && <p className="text-[11px] mt-1" style={{ color: '#C79A4A' }}>{p.trialDays}-day free trial</p>}
                  {(p.features || []).length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {(p.features || []).slice(0, 5).map((f, i) => (
                        <li key={i} className="flex items-center gap-1.5 text-[12px] text-stone-700">
                          <Check className="w-3.5 h-3.5" style={{ color: '#C79A4A' }} />{f}
                        </li>
                      ))}
                    </ul>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Coming-soon confirmation replaces live billing */}
      {showComingSoon && (
        <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowComingSoon(false)}>
          <div className="w-[90%] max-w-[360px] bg-[#FFFCF3] rounded-2xl p-5 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="membership-coming-soon">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-5 h-5" style={{ color: '#C79A4A' }} />
              <p className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: '#C79A4A' }}>Coming soon</p>
            </div>
            <p className="text-lg leading-snug text-stone-800" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Premium checkout is on the way.
            </p>
            <p className="text-[12px] text-stone-500 mt-2">
              Rowdy is finalizing membership pricing and billing. We'll email you when Premium is ready to purchase.
            </p>
            <button
              onClick={() => setShowComingSoon(false)}
              data-testid="membership-coming-soon-close"
              className="w-full mt-4 px-4 py-2.5 rounded-xl text-sm font-semibold"
              style={{ background: '#173F35', color: '#FFFCF6' }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Manage / Restore (placeholders) */}
      {isPremium && (
        <div className="px-5 mt-6 space-y-2">
          <button
            data-testid="membership-manage"
            onClick={() => setShowComingSoon(true)}
            className="w-full px-5 py-3 rounded-xl text-sm font-semibold border border-stone-200 bg-[#FFFCF6] text-stone-800 hover:border-[#C79A4A]"
          >
            Manage membership
          </button>
          <p className="text-[10px] text-stone-400 text-center">Cancellations remain respectful. Your access continues through the paid period.</p>
        </div>
      )}
      {status === 'EXPIRED' && (
        <div className="px-5 mt-6">
          <button data-testid="membership-restart" onClick={() => setShowComingSoon(true)} className="w-full px-5 py-3 rounded-xl text-sm font-semibold" style={{ background: '#173F35', color: '#FFFCF6' }}>
            Restart Premium
          </button>
        </div>
      )}
    </div>
  );
};

export default MembershipPage;
